import { TextareaHTMLAttributes, forwardRef, useState } from 'react';

export interface FormTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean;
  fullWidth?: boolean;
  showCharCount?: boolean;
  maxCharCount?: number;
}

/**
 * Styled textarea component with optional character count
 */
export const FormTextarea = forwardRef<HTMLTextAreaElement, FormTextareaProps>(
  ({ error, fullWidth = true, showCharCount, maxCharCount, className = '', value, ...props }, ref) => {
    const [charCount, setCharCount] = useState(String(value || '').length);

    const baseClasses = 'rounded-md border px-3 py-2 text-sm transition-colors resize-y';
    const widthClasses = fullWidth ? 'w-full' : '';
    const stateClasses = error
      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
      : 'border-zinc-300 focus:border-blue-500 focus:ring-blue-500';
    const interactionClasses = 'focus:outline-none focus:ring-2 focus:ring-opacity-50';
    const disabledClasses = props.disabled ? 'bg-zinc-100 cursor-not-allowed' : 'bg-white';

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (showCharCount || maxCharCount) {
        setCharCount(e.target.value.length);
      }
      props.onChange?.(e);
    };

    const isOverLimit = maxCharCount !== undefined && charCount > maxCharCount;

    return (
      <div data-tsx-id="form-textarea" className="space-y-1">
        <textarea
          ref={ref}
          className={`${baseClasses} ${widthClasses} ${stateClasses} ${interactionClasses} ${disabledClasses} ${className}`}
          value={value}
          onChange={handleChange}
          {...props}
        />
        {(showCharCount || maxCharCount) && (
          <div className={`text-xs text-right ${isOverLimit ? 'text-red-600' : 'text-zinc-500'}`}>
            {charCount}{maxCharCount && ` / ${maxCharCount}`}
          </div>
        )}
      </div>
    );
  }
);

FormTextarea.displayName = 'FormTextarea';
